import React from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader } from './AppHeader';
import { AppFooter } from './AppFooter';
import CommandPalette from '../components/CommandPalette';
import KeyboardShortcuts from '../components/KeyboardShortcuts';
import { ProfitCelebration } from '../components/ProfitCelebration';
import { WsOfflineBanner } from '../components/WsOfflineBanner';
import { AlertNotifications } from '../components/AlertNotifications';
import { TradeSounds } from '../components/TradeSounds';
import { isEmbed, fullSiteHref } from '../utils/embed';
import { useLocation, useNavigate } from 'react-router-dom';
import { tgWebApp } from '../utils/telegram';

/** Кнопка «Назад» в шапке Telegram: на любом экране, кроме первого. */
const TgBackButton: React.FC = () => {
  const loc = useLocation();
  const nav = useNavigate();
  React.useEffect(() => {
    const wa = tgWebApp();
    if (!wa?.BackButton) return;
    const back = () => nav(-1);
    if (loc.key !== 'default') wa.BackButton.show(); else wa.BackButton.hide();
    wa.BackButton.onClick(back);
    return () => wa.BackButton.offClick(back);
  }, [loc.key, nav]);
  return null;
};

/** Панель внутри чужой страницы (см. utils/embed): без шапки, футера,
 *  палитры и шорткатов — только страница и строка «открыть на сайте».
 *  Алерты со звуком тоже не здесь: их уже играет основная вкладка сайта. */
const EmbedShell: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation('appShell');
  return (
  <div className="relative flex min-h-screen flex-col bg-background text-foreground">
    <WsOfflineBanner />
    <TgBackButton />
    <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
      <span className="font-semibold text-foreground">spread.bot</span>
      <a href={fullSiteHref()} target="_blank" rel="noopener noreferrer" className="hover:text-foreground">
        {t('openOnSite')}
      </a>
    </div>
    <main className="relative z-10 flex-1">{children}</main>
  </div>
  );
};

export const AppShell: React.FC<React.PropsWithChildren> = ({ children }) =>
  isEmbed() ? <EmbedShell>{children}</EmbedShell> : (
  <div className="relative flex min-h-screen flex-col bg-background text-foreground">
    {/* Индикация обрыва WS — иначе замершие цены/стаканы выглядят живыми */}
    <WsOfflineBanner />
    <AppHeader />
    <main className="relative z-10 flex-1">{children}</main>
    {/* Футер на мобиле получает дополнительный bottom-padding под высоту
        fixed bottom-nav (h-16 ~ 64px + safe-area). Без этого футер обрезался
        мобильной нав-баром и Spreadbot-лого не было видно. */}
    <div className="relative z-0 pb-20 lg:pb-0">
      <AppFooter />
    </div>
    {/* Cmd+K палитра — глобально, реагирует на hotkey из любой страницы. */}
    <CommandPalette />
    {/* Vim-style "g s" нав-шорткаты + "?" help overlay */}
    <KeyboardShortcuts />
    {/* Конфетти когда engine присылает close_filled с положительным PnL */}
    <ProfitCelebration />
    {/* Браузерные уведомления + звук по алертам (работают и с фоновой вкладкой) */}
    <AlertNotifications />
    {/* Звук на заливку и выливку + сообщение о новой грамоте */}
    <TradeSounds />
  </div>
);
